<template>
    <div class="corrections-thread" :class="{ 'corrections-thread--reviewer': reviewer }">
        <button
            v-if="!reviewer || !isTwoColumnLayout"
            type="button"
            class="button button--gray corrections-thread__back"
            style="max-width: 300px"
            @click="back"
        >
            ‹ Back to list
        </button>
        <template v-if="correction">
            <div v-if="reviewer && isTwoColumnLayout" class="corrections-thread__heading">
                <h2 class="corrections-thread__title">Comments</h2>
                <button
                    type="button"
                    class="corrections-thread__close"
                    aria-label="Close comments"
                    @click="back"
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path
                            d="m6 6 12 12M18 6 6 18"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                        />
                    </svg>
                </button>
            </div>
            <h2 v-else class="corrections-thread__title">Comments</h2>
            <CorrectionsListItem
                v-if="!reviewer || !isTwoColumnLayout || !contextInList"
                :correction="correction"
                :reviewer-card="reviewer"
                @view-in-book="$emit('view-in-book', $event)"
                @update="update"
                @deleted="onDelete"
                @selected="select"
            />
            <div class="corrections-thread__thread corrections-thread">
                <PaginatedResource ref="threarResource" :fetch-method="fetch">
                    <template #default="{ resource: replies, canLoadMore, loadMore, loading }">
                        <div class="corrections-thread__replies">
                            <Spinner v-if="loading" />
                            <template v-else>
                                <div
                                    v-if="Math.abs(replies.length - 1)"
                                    class="corrections-thread__replies-count"
                                >
                                    {{ replies.length - 1 }}
                                    {{
                                        pluralize(replies.length - 1, [
                                            'previous message',
                                            'previous messages',
                                            'previous messages',
                                        ])
                                    }}
                                </div>
                                <div v-else class="corrections-thread__replies-count">
                                    No previous messages
                                </div>
                                <div>
                                    <div
                                        v-for="reply in replies"
                                        :key="reply.id"
                                        class="corrections-comment"
                                    >
                                        <span v-html="reply.comment" />
                                        <span class="corrections-comment__timestamp">
                                            {{ formatLocaleTimestamp(reply.created, $i18n.locale) }}
                                        </span>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </template>
                </PaginatedResource>
            </div>
        </template>
        <Spinner v-else-if="loading" />
    </div>
</template>

<script>
import PaginatedResource from '@speechki-frontend/shared/PaginatedResource';

import CorrectionsListItem from '../CorrectionsListItem/CorrectionsListItem';

import { fetchCorrectionThread } from '../../api/corrections';

import { pluralize, formatLocaleTimestamp } from '@speechki-frontend/shared/helpers/functions';

export default {
    name: 'CorrectionsThread',
    components: {
        CorrectionsListItem,
        PaginatedResource,
    },
    emits: ['back', 'update', 'select', 'delete', 'view-in-book'],
    props: {
        contextInList: {
            type: Boolean,
            default: false,
        },
        reviewer: {
            type: Boolean,
            default: false,
        },
        correction: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isTwoColumnLayout: false,
            layoutQuery: null,
        };
    },
    watch: {
        reviewer: {
            immediate: true,
            handler(reviewer) {
                this.layoutQuery?.removeEventListener('change', this.onLayoutChange);
                this.layoutQuery = null;
                this.isTwoColumnLayout = false;

                if (!reviewer) return;

                // Match CorrectionsPage.scss exactly: the left context card is
                // visible from 1401px. Only omit the right context when the
                // same correction is actually in that rendered list.
                this.layoutQuery = window.matchMedia('(min-width: 1401px)');
                this.isTwoColumnLayout = this.layoutQuery.matches;
                this.layoutQuery.addEventListener('change', this.onLayoutChange);
            },
        },
    },
    beforeUnmount() {
        this.layoutQuery?.removeEventListener('change', this.onLayoutChange);
    },
    methods: {
        pluralize,
        formatLocaleTimestamp,
        onLayoutChange(event) {
            this.isTwoColumnLayout = event.matches;
        },
        back() {
            this.$emit('back');
        },
        fetch(params) {
            return fetchCorrectionThread(this.correction.thread, params);
        },
        update(id) {
            if (this.reviewer && id && typeof id === 'object') {
                this.$emit('update', id);
                return;
            }

            this.$emit('update', this.reviewer ? { id, correctionId: this.correction.id } : id);
        },
        select(event) {
            this.$emit('select', event);
        },
        onDelete() {
            this.$emit('delete', this.correction.id);
            this.back();
        },
    },
};
</script>

<style lang="scss" src="./CorrectionsThread.scss"></style>
